import { useEffect, useState } from 'react';
import { pastorApi, errorMessage } from '../pastorApi';
import type { VerifyChannel } from '../pastorApi';

interface Props {
  channel: VerifyChannel;
  target: string;
  /** 알림톡 '#{고객명}' 에 들어갈 이름 */
  name?: string;
  /** 형식이 맞아야 [인증번호 받기]를 누를 수 있다 */
  valid: boolean;
  verified: boolean;
  onVerified: (target: string) => void;
}

const mmss = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

// 휴대전화(카카오 알림톡)·이메일 인증번호 받기 버튼 + 입력 행 — 입력칸 옆에 둔다
export function useVerifyCode({ channel, target, name, valid, verified, onVerified }: Props) {
  const [sentTo, setSentTo] = useState('');
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const left = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const pending = Boolean(sentTo) && sentTo === target && !verified;

  useEffect(() => {
    if (!pending || left === 0) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pending, left]);

  const send = async () => {
    setBusy(true);
    setMessage('');
    try {
      const { ttlSec } = await pastorApi.sendVerify(channel, target, name);
      setSentTo(target);
      setCode('');
      setExpiresAt(Date.now() + ttlSec * 1000);
      setNow(Date.now());
      setMessage(channel === 'phone' ? '카카오톡으로 인증번호를 보냈습니다.' : '메일로 인증번호를 보냈습니다. 받은편지함과 스팸함을 확인해 주세요.');
    } catch (e) {
      setMessage(errorMessage(e, '인증번호를 보내지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const check = async () => {
    setBusy(true);
    setMessage('');
    try {
      await pastorApi.checkVerify(channel, target, code);
      setSentTo('');
      onVerified(target);
    } catch (e) {
      setMessage(errorMessage(e, '인증하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const button = verified ? (
    <span className="pc-verified">인증 완료</span>
  ) : (
    <button type="button" className="mm-btn mm-btn--outline pc-btn-md" disabled={!valid || busy} onClick={send}>
      {pending ? '다시 받기' : '인증번호 받기'}
    </button>
  );

  const row = (
    <>
      {pending && (
        <div className="pc-inline pc-verify">
          <input
            className="mm-field"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
            onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) { e.preventDefault(); check(); } }}
            placeholder="인증번호 6자리"
            inputMode="numeric"
            autoComplete="one-time-code"
          />
          <span className="pc-verify__timer">{left > 0 ? mmss(left) : '만료'}</span>
          <button type="button" className="mm-btn mm-btn--primary pc-btn-md" disabled={code.length !== 6 || left === 0 || busy} onClick={check}>확인</button>
        </div>
      )}
      {message && !verified && <small className="pc-hint">{message}</small>}
    </>
  );

  return { button, row };
}
